import axios from 'axios'

const SITE_URL = String(process.env.VUE_APP_SITE_URL || 'https://www.gzzhyun.com').replace(/\/$/, '')
const LOCALE_PATHS = {
  'zh-CN': 'zh-cn',
  'en-US': 'en'
}

function ensureLeadingSlash (value) {
  const path = String(value || '/')
  return path.startsWith('/') ? path : `/${path}`
}

function absoluteUrl (value) {
  const url = String(value || '').trim()
  if (!url) return ''
  if (/^(?:https?:|data:|blob:)/i.test(url)) return url
  return `${SITE_URL}${ensureLeadingSlash(url)}`
}

export function localePath (locale) {
  return LOCALE_PATHS[locale] || LOCALE_PATHS['zh-CN']
}

export function localeFromPath (path) {
  const match = ensureLeadingSlash(path).match(/^\/(zh-cn|en)(?:\/|$)/i)
  if (!match) return null
  return match[1].toLowerCase() === 'en' ? 'en-US' : 'zh-CN'
}

export function logicalPath (path) {
  const value = ensureLeadingSlash(path).replace(/\/$/, '') || '/'
  return value.replace(/^\/(?:zh-cn|en)(?=\/|$)/i, '') || '/'
}

export function localizedPath (path, locale) {
  const logical = logicalPath(path)
  return `/${localePath(locale)}${logical === '/' ? '/' : `${logical}/`}`
}

function setMeta (name, content, property) {
  const selector = property
    ? `meta[property="${property}"]`
    : `meta[name="${name}"]`
  const elements = Array.from(document.head.querySelectorAll(selector))
  let element = elements.shift()
  elements.forEach(item => item.remove())
  if (!element) {
    element = document.createElement('meta')
    if (property) element.setAttribute('property', property)
    else element.setAttribute('name', name)
    document.head.appendChild(element)
  }
  element.setAttribute('data-seo', 'true')
  element.setAttribute('content', content || '')
}

function removeMeta (name, property) {
  const selector = property
    ? `meta[property="${property}"]`
    : `meta[name="${name}"]`
  document.head.querySelectorAll(selector).forEach(item => item.remove())
}

function setLink (rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`
  const elements = Array.from(document.head.querySelectorAll(selector))
  let element = elements.shift()
  elements.forEach(item => item.remove())
  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', rel)
    if (hreflang) element.setAttribute('hreflang', hreflang)
    document.head.appendChild(element)
  }
  element.setAttribute('data-seo', 'true')
  element.setAttribute('href', href)
}

function setJsonLd (value) {
  const elements = Array.from(document.head.querySelectorAll('script[type="application/ld+json"]'))
  let element = elements.find(item => item.getAttribute('data-seo-jsonld') === 'true') || elements[0]
  elements.filter(item => item !== element).forEach(item => item.remove())
  if (!element) {
    element = document.createElement('script')
    element.type = 'application/ld+json'
    document.head.appendChild(element)
  }
  element.setAttribute('data-seo', 'true')
  element.setAttribute('data-seo-jsonld', 'true')
  element.textContent = JSON.stringify(value)
}

export function applyPageSeo ({
  locale = 'zh-CN',
  path = '/',
  title,
  description,
  image,
  type = 'WebPage',
  headline,
  videoUrl,
  uploadDate
}) {
  const canonicalPath = localizedPath(path, locale)
  const canonical = `${SITE_URL}${canonicalPath}`
  const language = locale === 'en-US' ? 'en' : 'zh-CN'
  const alternatePath = logicalPath(path)

  document.documentElement.setAttribute('lang', language)
  document.title = title
  setMeta('description', description)
  setMeta('', title, 'og:title')
  setMeta('', description, 'og:description')
  setMeta('', canonical, 'og:url')
  if (image) setMeta('', absoluteUrl(image), 'og:image')
  else removeMeta('', 'og:image')
  setMeta('', type === 'Article' ? 'article' : 'website', 'og:type')
  setLink('canonical', canonical)
  setLink('alternate', `${SITE_URL}${localizedPath(alternatePath, 'en-US')}`, 'en')
  setLink('alternate', `${SITE_URL}${localizedPath(alternatePath, 'zh-CN')}`, 'zh-CN')
  setLink('alternate', `${SITE_URL}${localizedPath(alternatePath, 'en-US')}`, 'x-default')

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': type,
    name: title,
    headline: headline || title,
    description,
    url: canonical,
    inLanguage: language,
    publisher: {
      '@type': 'Organization',
      name: locale === 'en-US' ? 'Guangzhou Zhihui Cloud Technology Co., Ltd.' : '广州智会云科技有限公司',
      url: SITE_URL
    }
  }
  if (image) jsonLd.image = absoluteUrl(image)
  if (type === 'VideoObject') {
    if (image) jsonLd.thumbnailUrl = absoluteUrl(image)
    if (videoUrl) jsonLd.contentUrl = absoluteUrl(videoUrl)
    if (uploadDate) jsonLd.uploadDate = uploadDate
  }
  setJsonLd(jsonLd)
}

export function defaultPageSeo (locale) {
  const english = locale === 'en-US'
  return {
    locale,
    path: '/',
    title: english
      ? 'Guangzhou Zhihui Cloud Technology Co., Ltd. | Digital Meeting Systems'
      : '广州智会云科技有限公司 | 数字会议系统',
    description: english
      ? 'Paperless conference, electronic nameplate, conference microphone and speech transcription systems for modern meeting spaces.'
      : '提供无纸化会议、电子桌牌、会议话筒和语音转写等数字会议系统解决方案。'
  }
}

export function fetchPublicSeo (locale, kind, slug) {
  const baseURL = process.env.NODE_ENV === 'development' ? process.env.VUE_APP_BASE_URL : ''
  const pathLocale = localePath(locale)
  const endpoint = `/api/public/v1/${pathLocale}/${kind}/${encodeURIComponent(slug)}`
  return axios.get(endpoint, { baseURL, timeout: 8000 }).then(response => response.data)
}
